import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { services } from "@/data/siteContent";
import { Reveal } from "@/components/common/Reveal";

const SectionBlock = ({ title, items, testidPrefix }) => (
  <section className="rounded-2xl border border-blue-100/20 bg-card/75 p-6" data-testid={`${testidPrefix}-section`}>
    <h2 className="font-oswald text-2xl uppercase text-white" data-testid={`${testidPrefix}-heading`}>
      {title}
    </h2>
    <ul className="mt-4 space-y-3" data-testid={`${testidPrefix}-list`}>
      {items.map((item, index) => (
        <li
          key={`${testidPrefix}-${index}`}
          className="text-sm text-blue-100/88"
          data-testid={`${testidPrefix}-item-${index + 1}`}
        >
          {item}
        </li>
      ))}
    </ul>
  </section>
);

export default function ServiceCaseStudyPage() {
  const { slug } = useParams();
  const service = services.find((item) => item.slug === slug);

  if (!service) {
    return (
      <div className="mx-auto w-full max-w-7xl pb-16" data-testid="service-case-study-not-found">
        <div className="rounded-2xl border border-blue-100/20 bg-card/75 p-8">
          <h1 className="font-oswald text-3xl uppercase text-white" data-testid="service-case-study-not-found-title">
            Case Study Not Found
          </h1>
          <p className="mt-3 text-sm text-blue-100/88" data-testid="service-case-study-not-found-text">
            This case study route is unavailable. Please go back to the Services page.
          </p>
          <Link
            to="/services"
            className="mt-6 inline-flex items-center gap-2 font-oswald text-xs uppercase tracking-[0.16em] text-accent"
            data-testid="service-case-study-back-link"
          >
            <ArrowLeft size={14} /> Back To Services
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-7xl space-y-8 pb-16" data-testid="service-case-study-page">
      <Reveal>
        <Link
          to="/services"
          className="inline-flex items-center gap-2 font-oswald text-xs uppercase tracking-[0.16em] text-accent"
          data-testid="service-case-study-back-link"
        >
          <ArrowLeft size={14} /> Back To Services
        </Link>
      </Reveal>

      <Reveal delay={0.05}>
        <section className="rounded-2xl border border-blue-100/20 bg-card/75 p-7" data-testid="service-case-study-hero">
          <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between" data-testid="service-case-study-hero-content">
            <div className="max-w-4xl">
              <p className="font-oswald text-xs uppercase tracking-[0.2em] text-accent" data-testid="service-case-study-kicker">
                Case Study
              </p>
              <h1 className="mt-4 font-oswald text-4xl uppercase text-white sm:text-5xl" data-testid="service-case-study-title">
                {service.case_study.title}
              </h1>
              <p className="mt-4 text-sm text-blue-100/88 sm:text-base" data-testid="service-case-study-subtitle">
                {service.case_study.subtitle}
              </p>
            </div>

            {service.case_study.media?.url ? (
              <article
                className={`w-full rounded-xl border border-blue-100/20 bg-[#041748]/72 p-3 ${
                  service.case_study.media.size === "large"
                    ? "max-w-[230px]"
                    : service.case_study.media.size === "medium"
                      ? "max-w-[210px]"
                      : "max-w-[180px]"
                }`}
                data-testid="service-case-study-media-card"
              >
                <img
                  src={service.case_study.media.url}
                  alt={service.case_study.media.label || "Case study media"}
                  className={`w-full rounded-md object-contain object-center ${
                    service.case_study.media.size === "large"
                      ? "h-28"
                      : service.case_study.media.size === "medium"
                        ? "h-24"
                        : "h-20"
                  }`}
                  data-testid="service-case-study-media-image"
                />
                <p className="mt-2 text-center font-oswald text-[10px] uppercase tracking-[0.14em] text-blue-100/80" data-testid="service-case-study-media-label">
                  {service.case_study.media.label || "Featured"}
                </p>
              </article>
            ) : null}
          </div>
        </section>
      </Reveal>

      <div className="grid gap-5 lg:grid-cols-3" data-testid="service-case-study-main-grid">
        <Reveal delay={0.08}>
          <SectionBlock title="Challenge" items={service.case_study.challenge} testidPrefix="service-case-study-challenge" />
        </Reveal>
        <Reveal delay={0.12}>
          <SectionBlock title="Strategy" items={service.case_study.strategy} testidPrefix="service-case-study-strategy" />
        </Reveal>
        <Reveal delay={0.16}>
          <SectionBlock title="Execution" items={service.case_study.execution} testidPrefix="service-case-study-execution" />
        </Reveal>
      </div>

      <Reveal delay={0.2}>
        <section className="rounded-2xl border border-blue-100/20 bg-card/75 p-7" data-testid="service-case-study-outcomes-section">
          <h2 className="font-oswald text-2xl uppercase text-white" data-testid="service-case-study-outcomes-heading">
            Outcomes
          </h2>
          <div className="mt-5 grid gap-4 sm:grid-cols-2" data-testid="service-case-study-outcomes-grid">
            {service.case_study.outcomes.map((outcome, index) => (
              <article
                key={`${outcome.label}-${index}`}
                className="rounded-xl border border-blue-100/20 bg-[#05215e]/55 p-4"
                data-testid={`service-case-study-outcome-card-${index + 1}`}
              >
                <p className="font-oswald text-xs uppercase tracking-[0.16em] text-accent" data-testid={`service-case-study-outcome-label-${index + 1}`}>
                  {outcome.label}
                </p>
                <p className="mt-2 text-sm text-white" data-testid={`service-case-study-outcome-value-${index + 1}`}>
                  {outcome.value}
                </p>
              </article>
            ))}
          </div>
        </section>
      </Reveal>
    </div>
  );
}
